import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Sparkles } from 'lucide-react';

export default function Blog() {
  const posts = [
    {
      title: 'How AI is Transforming Education Technology',
      category: 'AI & Innovation',
      date: 'Jan 15, 2026',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80',
      excerpt: 'Explore how artificial intelligence is revolutionizing the way students learn and teachers educate in modern classrooms.'
    },
    {
      title: '10 Essential Features for Modern Mobile Apps',
      category: 'Mobile Development',
      date: 'Jan 10, 2026',
      image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&q=80',
      excerpt: 'Discover the must-have features that make mobile applications successful in today\'s competitive market.'
    },
    {
      title: 'Cloud Migration: A Complete Guide for Enterprises',
      category: 'Cloud Solutions',
      date: 'Jan 05, 2026',
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80',
      excerpt: 'Learn the step-by-step process of migrating your enterprise infrastructure to the cloud seamlessly.'
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: index * 0.08, ease: 'easeOut' }
    })
  };

  return (
    <section id="blog" className="py-24 md:py-32 relative bg-[#F8F5F0]" data-testid="blog-section">
      {/* Background elements */}
      <div className="absolute top-1/3 left-1/3 glowing-blob blob-beige opacity-[0.05]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-[#8B6B4A]/5 border border-[#8B6B4A]/10 rounded-full px-4 py-1.5 backdrop-blur-md mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#C8A97E]" />
            <span className="text-xs font-semibold tracking-wider uppercase text-[#8B6B4A] font-poppins">
              Industry Knowledge
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight" data-testid="blog-heading">
            Latest Insights & Guides
          </h2>
          <p className="text-[#6B625B] text-base sm:text-lg font-poppins max-w-2xl mx-auto mt-4" data-testid="blog-description">
            Read engineering tutorials, UI/UX breakdowns, and product scaling advice written by our senior developers.
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.title}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              className="group bg-white/40 border border-[#8B6B4A]/10 hover:border-[#8B6B4A]/30 rounded-3xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col justify-between"
              data-testid={`blog-post-${index}`}
            >
              
              <div>
                {/* Image Wrap */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                    loading="lazy"
                    data-testid={`blog-post-image-${index}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#F8F5F0] to-transparent opacity-90" />
                  
                  {/* Category Tag */}
                  <span className="absolute top-4 left-4 px-3 py-1 bg-white border border-[#8B6B4A]/10 rounded-full text-xs font-semibold text-[#3E2F24] font-poppins shadow-sm" data-testid={`blog-post-category-${index}`}>
                    {post.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-[#6B625B] mb-3 font-poppins">
                    <Calendar className="w-3.5 h-3.5 text-[#8B6B4A]" />
                    <span data-testid={`blog-post-date-${index}`}>{post.date}</span>
                  </div>

                  <h3 className="text-base sm:text-lg font-bold font-space text-[#3E2F24] mb-3 group-hover:text-[#8B6B4A] transition-colors" data-testid={`blog-post-title-${index}`}>
                    {post.title}
                  </h3>

                  <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed" data-testid={`blog-post-excerpt-${index}`}>
                    {post.excerpt}
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="px-6 pb-6 pt-2">
                <button className="inline-flex items-center gap-1.5 text-xs font-bold text-[#8B6B4A] hover:text-[#3E2F24] group-hover:gap-2.5 transition-all font-poppins uppercase tracking-wider" data-testid={`blog-post-read-more-${index}`}>
                  Read Full Post
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

            </motion.article>
          ))}
        </div>

      </div>
    </section>
  );
}